/**
 * <caldera-tabs>
 *
 * Generic WAI-ARIA Tabs implementation shared across Caldera tabbed sections.
 * Each section uses its own BEM class prefix and tab-button template by
 * setting attributes on the host element:
 *
 *   <caldera-tabs
 *     data-class-prefix="caldera-callout"     // BEM prefix for tab/panel classes
 *     data-tab-template="number-label"        // "label" | "number-label"
 *     data-emit-resize="true"                 // optional: dispatch window resize
 *                                              //   after activation (needed by
 *                                              //   panels containing Horizon
 *                                              //   slideshow-component)
 *   >
 *
 * The section renders an empty `[data-tablist]` container plus one block
 * wrapper per tab. Each block wrapper exposes the tab's metadata as data
 * attributes so this element can build tab buttons in JS without reading
 * Liquid-side block settings:
 *
 *   data-tab-id           — stable key matching the panel's data-tab-panel
 *   data-tab-label        — visible label text (hidden when blank)
 *   data-tab-number       — optional, used by the "number-label" template
 *   data-tab-description  — optional inline rich text (HTML), rendered below the
 *                            label and hidden when blank
 *   data-tab-button-id    — id to assign to the generated button (matches the
 *                            panel's aria-labelledby)
 *   data-tab-panel-id     — id of the panel (matches the button's aria-controls)
 *
 * Active state is reflected via aria-selected, tabindex (roving), and the
 * `${prefix}__tab--active` / `${prefix}__panel--active` classes. Hidden panels
 * use the `hidden` attribute so they collapse out of layout — important when
 * panels contain components that cache scroll metrics on connect.
 */
class CalderaTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {string} */
  #classPrefix = 'caldera-tabs';

  /** @type {'label' | 'number-label'} */
  #tabTemplate = 'label';

  /** @type {boolean} */
  #emitResize = false;

  connectedCallback() {
    this.#classPrefix = this.dataset.classPrefix || 'caldera-tabs';
    const template = this.dataset.tabTemplate;
    this.#tabTemplate = template === 'number-label' ? 'number-label' : 'label';
    this.#emitResize = this.dataset.emitResize === 'true';

    const tablist = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-tablist]')
    );
    const blockEls = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-tab-id]'))
    );

    if (!tablist || blockEls.length === 0) return;

    this.#buildTabs(tablist, blockEls);

    this.#tabs = /** @type {HTMLButtonElement[]} */ (
      Array.from(tablist.querySelectorAll('[role="tab"]'))
    );
    this.#panels = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[role="tabpanel"]'))
    );

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKeydown);
    }
  }

  disconnectedCallback() {
    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKeydown);
    }
  }

  /**
   * @param {HTMLElement} tablist
   * @param {HTMLElement[]} blockEls
   */
  #buildTabs(tablist, blockEls) {
    const fragment = document.createDocumentFragment();
    const tabClass = `${this.#classPrefix}__tab`;
    const tabActiveClass = `${this.#classPrefix}__tab--active`;

    for (const [index, blockEl] of blockEls.entries()) {
      const tabId = blockEl.dataset.tabId;
      const buttonId = blockEl.dataset.tabButtonId;
      const panelId = blockEl.dataset.tabPanelId;
      if (!tabId || !buttonId) continue;

      const isFirst = index === 0;
      const button = document.createElement('button');
      button.type = 'button';
      button.id = buttonId;
      button.className = tabClass;
      if (isFirst) button.classList.add(tabActiveClass);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      if (panelId) button.setAttribute('aria-controls', panelId);
      button.tabIndex = isFirst ? 0 : -1;
      button.dataset.tabTarget = tabId;

      this.#renderTabContent(button, blockEl);
      fragment.appendChild(button);
    }

    tablist.appendChild(fragment);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {HTMLElement} blockEl
   */
  #renderTabContent(button, blockEl) {
    const prefix = this.#classPrefix;
    const label = blockEl.dataset.tabLabel || '';
    const number = blockEl.dataset.tabNumber || '';
    const description = blockEl.dataset.tabDescription || '';

    if (this.#tabTemplate === 'number-label' && number) {
      const numberSpan = document.createElement('span');
      numberSpan.className = `${prefix}__tab-number`;
      numberSpan.textContent = number;
      button.appendChild(numberSpan);
    }

    if (label) {
      const labelSpan = document.createElement('span');
      labelSpan.className = `${prefix}__tab-label`;
      labelSpan.textContent = label;
      button.appendChild(labelSpan);
    }

    // Description is inline rich text (escaped into the data attribute on the
    // Liquid side), so render as HTML. Hidden when blank.
    if (description) {
      const descriptionSpan = document.createElement('span');
      descriptionSpan.className = `${prefix}__tab-description`;
      descriptionSpan.innerHTML = description;
      button.appendChild(descriptionSpan);
    }
  }

  /** @param {Event} event */
  #onTabClick = (event) => {
    const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
    this.#activate(tab);
  };

  /** @param {KeyboardEvent} event */
  #onTabKeydown = (event) => {
    const currentIndex = this.#tabs.indexOf(
      /** @type {HTMLButtonElement} */ (event.currentTarget)
    );
    if (currentIndex === -1) return;

    /** @type {number | null} */
    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    if (!nextTab) return;
    this.#activate(nextTab);
    nextTab.focus();
  };

  /** @param {HTMLButtonElement} activeTab */
  #activate(activeTab) {
    const targetId = activeTab.dataset.tabTarget;
    if (!targetId) return;

    const tabActiveClass = `${this.#classPrefix}__tab--active`;
    const panelActiveClass = `${this.#classPrefix}__panel--active`;

    for (const tab of this.#tabs) {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle(tabActiveClass, isActive);
    }

    for (const panel of this.#panels) {
      const isActive = panel.dataset.tabPanel === targetId;
      panel.toggleAttribute('hidden', !isActive);
      panel.classList.toggle(panelActiveClass, isActive);
    }

    // Horizon's slideshow-component (and others) cache scroll metrics on
    // connect, but inactive panels start as display:none — any nested
    // measurement reads 0. When a panel containing such a component becomes
    // visible, sections opt in via data-emit-resize="true" to nudge it.
    if (this.#emitResize) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }

    this.dispatchEvent(
      new CustomEvent('caldera-tabs:activate', {
        detail: { tabId: targetId, tab: activeTab },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('caldera-tabs')) {
  customElements.define('caldera-tabs', CalderaTabs);
}
